import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp, FaPlayCircle } from "react-icons/fa";
import videoSrc from "../../assets/videos/video.mp4";

function useHeroReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return [ref, visible];
}

function Reveal({ children, delayMs = 0, visible, className = "" }) {
  return (
    <div
      className={[
        visible
          ? "animate-fade-up motion-reduce:animate-none motion-reduce:opacity-100"
          : "opacity-0",
        className,
      ].join(" ")}
      style={{ animationDelay: visible ? `${delayMs}ms` : undefined }}
    >
      {children}
    </div>
  );
}

function Hero() {
  const [heroRef, visible] = useHeroReveal();

  return (
    <section
      ref={heroRef}
      className="relative min-h-[92vh] flex items-center overflow-hidden bg-surface text-white"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow" aria-hidden />
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,10,0.3)_0%,#0a0a0a_100%)]"
        aria-hidden
      />

      <div
        className="absolute top-1/4 -right-24 w-[min(75vw,420px)] h-[min(75vw,420px)] rounded-full bg-gradient-to-bl from-brand-orange/25 via-brand-amber/10 to-transparent blur-3xl animate-glow-orbs"
        aria-hidden
      />
      <div
        className="absolute -bottom-28 -left-20 w-[min(85vw,480px)] h-[min(85vw,480px)] rounded-full bg-gradient-to-tr from-brand-yellow/14 via-transparent to-transparent blur-3xl animate-glow-orbs [animation-delay:1.4s]"
        aria-hidden
      />

      <div className="container mx-auto px-4 relative z-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-xl lg:max-w-2xl lg:pl-4">
            <Reveal visible={visible} delayMs={0}>
              <p className="text-brand-amber/90 text-xs md:text-sm uppercase tracking-[0.2em] font-semibold mb-4">
                Mumbai&apos;s trusted signage partner
              </p>
            </Reveal>

            <Reveal visible={visible} delayMs={90}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] mb-3">
                <span className="block text-white/95">
                  Let&apos;s Build Your
                </span>
              </h1>
            </Reveal>

            <Reveal visible={visible} delayMs={180}>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] text-gradient-brand drop-shadow-[0_0_40px_rgba(245,158,11,0.25)] mt-1 mb-4">
                Dream Brand
              </span>
              <div
                className={[
                  "h-1 w-28 md:w-36 rounded-full bg-gradient-brand origin-left scale-x-0 mb-5",
                  visible
                    ? "animate-hero-line motion-reduce:scale-x-100 motion-reduce:animate-none"
                    : "",
                ].join(" ")}
                style={{ animationDelay: visible ? "420ms" : undefined }}
                aria-hidden
              />
            </Reveal>

            <Reveal visible={visible} delayMs={280}>
              <p className="text-lg md:text-xl text-gray-400 font-medium mb-3">
                DEFINE ⋆ DESIGN ⋆ DISPLAY
              </p>
            </Reveal>

            <Reveal visible={visible} delayMs={360}>
              <div className="flex gap-4 mb-8 max-w-lg items-stretch">
                <div
                  className={[
                    "w-1 shrink-0 rounded-full bg-gradient-to-b from-brand-orange via-brand-amber to-brand-yellow origin-top scale-y-0",
                    visible
                      ? "motion-reduce:!scale-y-100 animate-grow-bar motion-reduce:animate-none"
                      : "",
                  ].join(" ")}
                  style={{ animationDelay: visible ? "450ms" : undefined }}
                  aria-hidden
                />
                <p className="text-gray-500 text-sm md:text-base leading-relaxed italic">
                  &ldquo;Premium ACP Facades, 3D Acrylic & Steel Letters, &
                  Dynamic Video Panels that make your brand a landmark.&rdquo;
                </p>
              </div>
            </Reveal>

            <Reveal visible={visible} delayMs={440}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6">
                <Link
                  to="/inquiry"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-brand px-8 py-3.5 text-sm font-bold text-black shadow-brand-glow hover:opacity-95 transition-all hover:shadow-[0_0_36px_-4px_rgba(245,158,11,0.45)] active:scale-[0.98]"
                >
                  <FaPlayCircle
                    className="text-lg opacity-90 transition-transform duration-300 group-hover:scale-110"
                    aria-hidden
                  />
                  Inquiry Now
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center rounded-full border-2 border-brand-amber/60 text-brand-amber px-8 py-3.5 text-sm font-semibold hover:bg-brand-amber/10 hover:border-brand-yellow transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98]"
                >
                  Browse services
                </Link>
              </div>
            </Reveal>

            <Reveal visible={visible} delayMs={520}>
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group/wa inline-flex items-center gap-2 text-sm font-semibold text-brand-yellow hover:text-white transition-colors"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-black shadow-md transition-transform duration-300 group-hover/wa:scale-110 group-hover/wa:shadow-brand-glow">
                  <FaWhatsapp className="w-4 h-4" />
                </span>
                WhatsApp for instant quote
              </a>
            </Reveal>
          </div>

          <div
            className={[
              "w-fit max-w-full min-w-0 justify-self-center",
              "border-gradient-brand rounded-2xl bg-surface-elevated/90 backdrop-blur-md overflow-hidden shadow-brand-glow",
              visible
                ? "animate-scale-in motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:scale-100 opacity-0"
                : "opacity-0",
            ].join(" ")}
            style={{ animationDelay: visible ? "240ms" : undefined }}
          >
            <video
              src={videoSrc}
              autoPlay
              loop
              muted
              playsInline
              className="block h-auto w-auto max-h-[min(70vh,720px)] max-w-full object-contain object-center"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

export default Hero;
